import React, { useEffect, useRef, useState } from 'react';
import {
  Stack,
  Container,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  Button,
  TextField,
  Avatar,
  CircularProgress,
  Modal,
  Snackbar,
  Alert
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import { api } from 'src/Api';
import CloseIcon from '@mui/icons-material/Close';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { LoadingButton } from '@mui/lab';
import HTMLRenderer from 'react-html-renderer';

import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';
import { EmojiBlot, ShortNameEmoji, ToolbarEmoji } from 'quill-emoji';
import { TableNoData } from 'src/components/NoData';

export default function ChatScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { invitation_id } = location.state || {};
  const theme = useTheme();
  const userData = JSON.parse(localStorage.getItem('user'));
  const messagesEndRef = useRef(null); // Ref to track the last message

  const [open, setOpen] = useState(false);

  const [loadingMessage, setloadingMessage] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [messageData, setMessageData] = useState([]);
  const [message, setMessage] = useState('');

  // Scroll to the last message whenever the component renders or messageData changes
  useEffect(() => {
    if (messagesEndRef.current && !loadingMessage) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messageData, loadingMessage]);

  useEffect(() => {
    getMessages();
  }, []);

  function getMessages() {
    setloadingMessage(true);
    const myHeaders = new Headers();

    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow'
    };

    const queryParams = new URLSearchParams();
    queryParams.append('invitation_id', invitation_id);

    fetch(`${api.get_messages}?${queryParams.toString()}`, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200) {
          setMessageData(result.data);
          setloadingMessage(false);
        } else {
          setMessageData([]);
          setloadingMessage(false);
        }
      })
      .catch((error) => {
        console.log('error', error);
        setloadingMessage(false);
      });
  }

  function sendMessage() {
    setIsSaveLoading(true);
    const myHeaders = new Headers();

    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formdatas = new FormData();
    invitation_id && formdatas.append('invitation_id', invitation_id); // Required
    message && formdatas.append('message', message); // Required

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdatas,
      redirect: 'follow'
    };

    fetch(api.send_message, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          setSnackbarMessage(result.message);
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setIsSaveLoading(false);
          handleClose();
          getMessages();
        } else {
          setIsSaveLoading(false);
          setSnackbarMessage(result.message);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      })
      .catch((error) => {
        setIsSaveLoading(false);
        setSnackbarMessage('An error occurred.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClose = () => {
    setMessage('');
    setOpen(false);
  };

  return (
    <Page title={`Messages | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <IconButton
            style={{
              marginRight: 12,
              height: 40,
              width: 70,
              borderRadius: 5,
              backgroundColor: 'rgba(209,209,209,0.3)'
            }}
            onClick={() => navigate(-1)}
          >
            <Iconify icon="ion:return-up-back" style={{ fontSize: 25, color: '#010f58' }} />
          </IconButton>
          <Typography variant="h4" sx={{ flex: 1, color: '#010f58' }}>
            Messages
          </Typography>

          <Button
            variant="contained"
            startIcon={<Iconify icon="material-symbols:send-rounded" style={{ fontSize: 15 }} />}
            sx={{ alignSelf: 'end', fontSize: 11 }}
            onClick={() => setOpen(true)}
            // disabled={messageData.length === 0}
          >
            Send Message
          </Button>
        </Stack>

        <Container
          sx={{
            backgroundColor: '#EDEEEE',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            borderRadius: 1,
            height: '80vh',
            mb: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '8px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555'
            }
          }}
        >
          {/* Chat messages container */}
          {!loadingMessage && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                pt: 3,
                pb: 3
              }}
            >
              {messageData.map((msg) => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: msg?.from_user?.id === userData?.id ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end', // Center align avatar and message
                    gap: 2,
                    position: 'relative'
                  }}
                >
                  {/* Avatar for the message */}
                  {msg?.from_user?.id !== userData?.id && (
                    <Avatar src={msg?.to_user?.avatar} alt={msg?.to_user?.company} />
                  )}
                  <Box
                    sx={{
                      maxWidth: '60%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      backgroundColor: msg?.from_user?.id === userData?.id ? '#ffffff' : '#010f58',
                      color: msg?.from_user?.id === userData?.id ? '#000000' : '#ffffff',
                      whiteSpace: 'pre-wrap',
                      position: 'relative',
                      '&::after':
                        msg?.from_user?.id === userData?.id
                          ? {
                              content: '""',
                              position: 'absolute',
                              bottom: -10,
                              right: '-12.5px',
                              borderWidth: '20px 0 0 20px',
                              borderStyle: 'solid',
                              borderColor: 'transparent transparent transparent #ffffff',
                              transform: 'translateY(-50%)'
                            }
                          : {
                              content: '""',
                              position: 'absolute',
                              bottom: -10,
                              left: '-13px',
                              borderWidth: '20px 20px 0 0',
                              borderStyle: 'solid',
                              borderColor: 'transparent #010f58 transparent transparent',
                              transform: 'translateY(-50%)'
                            }
                    }}
                  >
                    <HTMLRenderer html={msg?.message} />
                  </Box>
                  {msg?.from_user?.id === userData?.id && (
                    <Avatar src={msg?.from_user?.avatar} alt={msg?.from_user?.company} />
                  )}
                </Box>
              ))}

              {/* Ref for the last message */}
              <div ref={messagesEndRef}></div>
            </Box>
          )}

          {loadingMessage && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!loadingMessage && messageData.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <TableNoData />
            </Box>
          )}
        </Container>

        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              flex: 1,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              bgcolor: 'background.paper',
              boxShadow: 24,
              padding: '5px 15px 15px 15px',
              borderRadius: 1
            }}
          >
            {/* Header with Close Button */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Send a Message</Typography>
              <IconButton onClick={handleClose} sx={{ marginRight: -1 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Rich Text Editor */}
            <ReactQuill
              modules={{
                toolbar: [
                  [{ header: [1, 2, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                  ['link', 'image'],
                  ['emoji'],
                  ['clean']
                ],
                'emoji-toolbar': true,
                'emoji-textarea': false,
                'emoji-shortname': true
              }}
              value={message}
              onChange={setMessage}
              placeholder="Write your message..."
              style={{ marginBottom: 20 }}
            />

            {/* Send Button */}
            <LoadingButton
              variant="contained"
              startIcon={<Iconify icon="material-symbols:send-rounded" />}
              sx={{ float: 'right' }}
              onClick={() => sendMessage()}
              disabled={!message.trim() || isSaveLoading}
              loading={isSaveLoading}
            >
              Send
            </LoadingButton>
          </Box>
        </Modal>

        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
}
